import { useEffect, useMemo, useState } from "react";
import Drawer from "@mui/material/Drawer";
import { Box, MenuItem } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import moment from "moment-timezone";

import { CustomCheckbox } from "./checkBox/CheckBox";
import Text from "./text/Text";
import {
  requestStatement,
  cancel,
  request,
  periodLabel,
  advanceFilters,
  asset,
  transactionType,
  dateOrder,
  transactionSource,
  select,
  documentFormat,
  selectAsset,
} from "./messages";
import CustomDivider from "./divider/Divider";
import CustomDateRangePicker from "./datepicker/CustomDateRangePicker";
import Button from "./button/Button";
import {
  dateOrderOptions,
  getCurrenciesOptions,
  getTimeZoneAndDateFormat,
  isTMA,
  reconciliationFormat,
  statementPeriodOptions,
  statmentDocumentFormatOptions,
  statmentTransactionTypeOptions,
  utcTimestampForDashboard,
} from "./constants";
import CustomSelect from "./customSelect/CustomSelect";
import TransactionSourceDrawer from "./TransactionSourceDrawer";
import { Tag } from "./tag/Tag";

function StatementGenerateDrawer({
  open,
  handleDrawerClose,
  handleGenerateStatement,
  isFromBrowser,
}) {
  const [disabled, setDisabled] = useState(true);
  const [openTransactionSourceDrawer, setOpenTransactionSourceDrawer] =
    useState(false);
  const [advanceFilter, setAdvanceFilter] = useState(false);

  const { accountAssets } = useSelector((state) => state.common);
  const currenciesOptions = getCurrenciesOptions(accountAssets);

  const currencyList = useMemo(
    () =>
      accountAssets?.map((assetData) => ({
        label: assetData.name,
        value: assetData.uuid,
      })),
    [accountAssets]
  );

  const initialValues = {
    statement_period: "last_month",
    start_at: null,
    end_at: null,
    target_currency: "all",
    transaction_type: "all",
    transaction_source: [],
    sort_order: "descending",
    document_type: "pdf",
    is_data_reconciliation_needed: false,
  };

  const formik = useFormik({
    initialValues,
  });

  const { values, isValid, isSubmitting, errors, setFieldValue } = formik;

  useEffect(() => {
    setGivenDateRangeValue({
      dateType: values.statement_period,
    });
  }, [values.statement_period]);

  useEffect(() => {
    isDisableRequestBtn();
  }, [
    isValid,
    values.statement_period,
    values.start_at,
    values.end_at,
    isSubmitting,
  ]);

  const isDisableRequestBtn = () => {
    const { statement_period, start_at, end_at } = values;
    const isValidPeriod =
      statement_period === "custom_date" ? start_at && end_at : true;

    setDisabled(!isValid || isSubmitting || !isValidPeriod);
  };

  const handleDelete = (index) => {
    setFieldValue(
      "transaction_source",
      values.transaction_source.filter((_, i) => i !== index)
    );
  };

  const advanceFilterSection = () => (
    <Box>
      <CustomSelect
        name="target_currency"
        customClass="dropdown"
        showLabel={true}
        value={values.target_currency}
        MenuProps={{
          id: "request-statement-asset",
        }}
        label={asset}
        onChange={(event) => {
          const {
            target: { value },
          } = event;
          setFieldValue("target_currency", value);
        }}
        renderValue={(selectedValue) => {
          if (!selectedValue) {
            return <Box>{selectAsset}</Box>;
          }
          const selectedOption = currenciesOptions.find(
            (option) => option.value === selectedValue
          );
          return selectedOption ? selectedOption.label : selectedValue;
        }}
      >
        {currenciesOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </CustomSelect>
      <CustomSelect
        name="transaction_type"
        customClass="dropdown mt-14"
        value={values.transaction_type}
        showLabel={true}
        label={transactionType}
        MenuProps={{
          id: "request-statement-asset",
        }}
        onChange={(event) => {
          const {
            target: { value },
          } = event;
          setFieldValue("transaction_type", value);
        }}
      >
        {statmentTransactionTypeOptions.map((option) => (
          <MenuItem key={option.id} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </CustomSelect>
      <CustomSelect
        readOnly
        customClass="dropdown mt-14"
        value={select}
        placeholder={select}
        showLabel={true}
        label={transactionSource}
        MenuProps={{
          id: "request-statement-asset",
        }}
        onClick={() => {
          setOpenTransactionSourceDrawer(true);
        }}
      >
        <MenuItem value={select}>{select}</MenuItem>
      </CustomSelect>
      <Box className="transaction-sources-sec">
        {values.transaction_source.map((tsource, index) => (
          <Tag
            key={index}
            text={tsource}
            sx={{
              backgroundColor: "#F0F5FA",
              color: "#0A193E",
              padding: "4px 8px",
            }}
            onDelete={() => handleDelete(index)}
          />
        ))}
      </Box>
      <CustomSelect
        name="sort_order"
        customClass="dropdown mt-14"
        value={values.sort_order}
        showLabel={true}
        label={dateOrder}
        MenuProps={{
          id: "request-statement-asset",
        }}
        onChange={(event) => {
          const {
            target: { value },
          } = event;
          setFieldValue("sort_order", value);
        }}
      >
        {dateOrderOptions.map((option) => (
          <MenuItem key={option.id} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </CustomSelect>
      <CustomDivider sx={{ margin: "16px 0px" }} />
    </Box>
  );

  const handleTransactionSourceDrawerClose = () => {
    setOpenTransactionSourceDrawer(false);
  };

  const setCustomDateRange = async ({ startDate = null, endDate = null }) => {
    await setFieldValue("start_at", startDate);
    await setFieldValue("end_at", endDate);
  };

  const getTimeValueForGivenTypeRange = (
    valueToSubstract,
    typeToSubstrect,
    isStartOfDay = true
  ) => {
    const { timezone } = getTimeZoneAndDateFormat();
    const givenTypeTimestamp = utcTimestampForDashboard({
      timezone,
      isStartDay: true,
      days: valueToSubstract,
      typeOfSubstraction: typeToSubstrect,
      operation: "subtract",
    });

    const methodName = isStartOfDay ? "startOf" : "endOf";
    return moment(givenTypeTimestamp)
      ?.[methodName]?.(typeToSubstrect)
      ?.valueOf();
  };

  const setLastMonthDateValue = () => {
    const startOfMonthTime = getTimeValueForGivenTypeRange(1, "months");
    const endOfMonthTime = getTimeValueForGivenTypeRange(1, "months", false);
    setCustomDateRange({
      startDate: startOfMonthTime,
      endDate: endOfMonthTime,
    });
  };

  const setGivenDateRangeValue = ({ dateType }) => {
    let startTime, endTime;
    switch (dateType) {
      case "last_month":
        setLastMonthDateValue();
        break;
      case "last_three_month":
        startTime = getTimeValueForGivenTypeRange(3, "months");
        endTime = getTimeValueForGivenTypeRange(1, "months", false);
        setCustomDateRange({
          startDate: startTime,
          endDate: endTime,
        });
        break;
      case "last_six_month":
        startTime = getTimeValueForGivenTypeRange(6, "months");
        endTime = getTimeValueForGivenTypeRange(1, "months", false);
        setCustomDateRange({
          startDate: startTime,
          endDate: endTime,
        });
        break;
      case "custom_date":
        setCustomDateRange({});
        break;
      default:
        setLastMonthDateValue();
        break;
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        className={`filter-drawer-wrapper statement-generate-drawer ${
          isFromBrowser ? "options-width" : "popup-width"
        }`}
        ModalProps={{
          disablePortal: !isTMA(),
        }}
      >
        <Box className="drawer-header">
          <ArrowBackIcon
            sx={{ cursor: "pointer" }}
            onClick={() => handleDrawerClose()}
          ></ArrowBackIcon>
          <Text className="default-text ml-8" size={18} font="bold">
            {requestStatement}
          </Text>
        </Box>
        <Box className="drawer-body" p="20px">
          <CustomSelect
            name="statement_period"
            customClass="dropdown mb-16"
            showLabel={true}
            label={periodLabel}
            value={values.statement_period}
            MenuProps={{
              id: "request-statement-asset",
            }}
            onChange={async (e) => {
              setFieldValue("statement_period", e.target.value);
              e.target.value === "custom_date"
                ? setCustomDateRange({})
                : setGivenDateRangeValue({
                    dateType: e.target.value,
                  });
            }}
          >
            {statementPeriodOptions.map((option) => (
              <MenuItem key={option.id} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </CustomSelect>
          {values.statement_period === "custom_date" && (
            <CustomDateRangePicker formik={formik} showDateLabel={false} />
          )}
          <Box>
            <CustomCheckbox
              className="advance-filter"
              name="advance-filter"
              label=""
              checked={advanceFilter}
              onChange={(e) => {
                setAdvanceFilter(!advanceFilter);
              }}
            />

            <Text
              className="default-text"
              font="medium"
              size={14}
              variant="caption"
            >
              {advanceFilters}
            </Text>
          </Box>
          <CustomDivider sx={{ margin: "16px 0px" }} />
          {advanceFilter && advanceFilterSection()}
          <CustomSelect
            name="document_type"
            customClass="dropdown mb-16"
            showLabel={true}
            label={documentFormat}
            value={values.document_type}
            MenuProps={{
              id: "request-statement-asset",
            }}
            onChange={(event) => {
              const {
                target: { value },
              } = event;
              setFieldValue("document_type", value);
            }}
          >
            {statmentDocumentFormatOptions.map((option) => (
              <MenuItem key={option.id} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </CustomSelect>

          <CustomDivider sx={{ margin: "16px 0px" }} />
          <Box display="flex" flexDirection="row" gap={0.5}>
            <CustomCheckbox
              className="reconciliation-format"
              name="reconciliation-format"
              label=""
              checked={values.is_data_reconciliation_needed}
              onChange={(e) => {
                setFieldValue(
                  "is_data_reconciliation_needed",
                  !values.is_data_reconciliation_needed
                );
              }}
            />

            <Text
              className="default-text"
              font="medium"
              size={14}
              variant="caption"
            >
              {reconciliationFormat}
            </Text>
          </Box>
        </Box>
        <Box
          className="drawer-footer"
          p="14px 20px"
          display="flex"
          justifyContent="space-between"
          border="1px solid #F6F3F3"
        >
          <Button
            variant="outlined"
            className="submit-btn"
            label={cancel}
            onClick={() => {
              handleDrawerClose();
            }}
            sx={{ width: "48%" }}
          />
          <Button
            variant="contained"
            className="submit-btn"
            label={request}
            onClick={() => {
              handleGenerateStatement(values);
            }}
            sx={{ width: "48%" }}
            disabled={disabled}
          />
        </Box>
      </Drawer>

      {openTransactionSourceDrawer && (
        <TransactionSourceDrawer
          open={openTransactionSourceDrawer}
          handleDrawerClose={handleTransactionSourceDrawerClose}
          isFromBrowser={isFromBrowser}
          values={values}
          formik={formik}
        />
      )}
    </>
  );
}

export default StatementGenerateDrawer;
